import {
  WHITE,
  contrastRatio,
  darken,
  darkenUntilContrast,
  isVeryDark,
  lighten,
  mixColors,
  parseColor,
  resolveAccessibleFill,
  toHex,
  toRgbaString,
} from './color';

// A school's public profile is branded with the `themeColor` returned by the
// API. Every accent on the page reads from the CSS custom properties built
// here, so the palette lives in exactly one place.
//
// When `themeColor` is missing or invalid we keep the previous behaviour: a
// stable colour derived from the school name.

// Text contrast target for accent colours used on white backgrounds.
const TEXT_CONTRAST_TARGET = 4.5;

// Near-black the page's ink is built from. Every neutral carries a trace of the
// school's colour so the greys read as chosen rather than inherited, and each
// school's profile sits on its own tint of paper.
const INK_BASE = { r: 20, g: 22, b: 26 };

const FALLBACK_ACCENTS = [
  '#1B4E8F',
  '#0F6E6E',
  '#6B2D6B',
  '#1F5C3A',
  '#3A3D8F',
  '#8A2B3E',
  '#9A5410',
];

export const DEFAULT_THEME_COLOR = FALLBACK_ACCENTS[0];

const hashString = (value) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 100000;
  }
  return hash;
};

/** Stable per-school colour used when the API sends no usable `themeColor`. */
const getFallbackAccent = (schoolName) => {
  const seed = typeof schoolName === 'string' ? schoolName.trim().toLowerCase() : '';
  if (!seed) return DEFAULT_THEME_COLOR;

  return FALLBACK_ACCENTS[hashString(seed) % FALLBACK_ACCENTS.length];
};

/**
 * Build the accent palette for a school.
 *
 * - `accent`      the school's colour exactly as given — decorative only
 *                 (rules, borders, dividers), never text on top of it
 * - `accentFill`  the colour used for solid fills that carry text; equals
 *                 `accent` unless no text colour could reach the contrast
 *                 target on it, in which case it is nudged the shortest way
 * - `accentOn`    text / icon colour placed on `accentFill`, light or dark
 *                 depending on how light the school's colour is
 * - `accentHover` hover state for solid fills
 * - `accentInk`   the accent darkened until it clears 4.5:1 on white — used for
 *                 links, icons, eyebrows and focus rings
 * - `accentStrong` a deeper shade for gradient ends and dark brand surfaces
 * - `accentSoft`  near-white tint for chips and icon backgrounds
 * - `accentBorder` light tint for branded borders and dividers
 * - `accentGlow`  translucent accent for shadows
 */
export const getSchoolTheme = ({ themeColor, schoolName } = {}) => {
  const parsed = parseColor(themeColor);
  const accent = parsed || parseColor(getFallbackAccent(schoolName));

  const ink = darkenUntilContrast(accent, WHITE, TEXT_CONTRAST_TARGET);
  const strong = darken(ink, 0.22);
  const { fill, text, adjusted } = resolveAccessibleFill(accent, TEXT_CONTRAST_TARGET);

  // Darkening an already near-black fill looks flat, so lift it instead.
  const hover = isVeryDark(fill) ? lighten(fill, 0.14) : darken(fill, 0.12);

  // Paper and ink: neutrals tinted with the school's hue.
  const paper = mixColors(ink, WHITE, 0.965);
  const paperRaised = mixColors(ink, WHITE, 0.925);
  const pageInk = mixColors(INK_BASE, accent, 0.1);
  const inkSoft = darkenUntilContrast(mixColors(pageInk, paper, 0.32), paper, 7);
  const inkMuted = darkenUntilContrast(mixColors(pageInk, paper, 0.52), paper, 4.5);

  return {
    isFromApi: Boolean(parsed),
    accent: toHex(accent),
    accentFill: toHex(fill),
    accentOn: toHex(text),
    accentHover: toHex(hover),
    accentInk: toHex(ink),
    accentStrong: toHex(strong),
    // Tints come from `ink`, not the raw accent: a pale brand colour mixed
    // into white would otherwise disappear against the white page.
    accentSoft: toHex(mixColors(ink, WHITE, 0.9)),
    accentBorder: toHex(mixColors(ink, WHITE, 0.72)),
    accentGlow: toRgbaString(accent, 0.3),
    // Page neutrals, all carrying a trace of the school's hue.
    paper: toHex(paper),
    paperRaised: toHex(paperRaised),
    rule: toHex(mixColors(ink, WHITE, 0.84)),
    ruleStrong: toHex(mixColors(ink, WHITE, 0.66)),
    pageInk: toHex(pageInk),
    inkSoft: toHex(inkSoft),
    inkMuted: toHex(inkMuted),
    // Shadows carry the accent hue rather than neutral black.
    shadowTint: toRgbaString(accent, 0.14),
    shadowDeep: toRgbaString(pageInk, 0.1),
    /** True when the fill had to be shifted to stay readable. */
    fillWasAdjusted: adjusted,
    /** Contrast of the raw accent against white, useful for tests / debugging. */
    accentContrastOnWhite: Number(contrastRatio(accent, WHITE).toFixed(2)),
  };
};

/** Up to two initials used for the logo fallback ("Queens School" -> "QS"). */
export const getSchoolInitials = (schoolName) => {
  if (typeof schoolName !== 'string') return '';

  const words = schoolName
    .trim()
    .split(/\s+/)
    .filter((word) => /[a-z0-9]/i.test(word));

  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

/**
 * CSS custom properties applied to the page root. Every school-profile
 * component consumes these variables rather than hardcoding colours.
 */
export const getSchoolThemeStyle = (profile = {}) => {
  const theme = getSchoolTheme(profile);

  return {
    '--sp-accent': theme.accent,
    '--sp-accent-fill': theme.accentFill,
    '--sp-accent-on': theme.accentOn,
    '--sp-accent-hover': theme.accentHover,
    '--sp-accent-ink': theme.accentInk,
    '--sp-accent-strong': theme.accentStrong,
    '--sp-accent-soft': theme.accentSoft,
    '--sp-accent-border': theme.accentBorder,
    '--sp-accent-glow': theme.accentGlow,
    '--sp-paper': theme.paper,
    '--sp-paper-raised': theme.paperRaised,
    '--sp-rule': theme.rule,
    '--sp-rule-strong': theme.ruleStrong,
    '--sp-ink': theme.pageInk,
    '--sp-ink-soft': theme.inkSoft,
    '--sp-ink-muted': theme.inkMuted,
    '--sp-shadow-tint': theme.shadowTint,
    '--sp-shadow-deep': theme.shadowDeep,
  };
};
